import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { client } from '../amplify-client';
import { useRolle } from '../auth/useRolle';
import { GuennieRating } from '../components/GuennieRating';
import { SpaghettiDivider } from '../components/SpaghettiDivider';
import { formatDatum } from '../lib/format';
import { uebersetzeSchreibFehler } from '../lib/fehler';
import { pflichtnotenVollstaendig } from '../../amplify/shared/guennies';
import type { Schema } from '../../amplify/data/resource';

const FELDER = [
  'id',
  'eisdieleId',
  'besuchsdatum',
  'status',
  'gesamtGuennies',
  'noteSpaghettiOptik',
  'noteKonsistenz',
  'noteGeschmack',
  'noteFruchtsauce',
] as const;

type Test = Pick<Schema['SpaghettiEisTest']['type'], (typeof FELDER)[number]>;
type EisdieleKurz = { id: string; name: string; ort: string };

const STATUS_LABEL: Record<string, string> = {
  ENTWURF: 'Entwurf',
  VEROEFFENTLICHT: 'Veröffentlicht',
};

/**
 * „Meine" — Übersicht der eigenen Verkostungen (Entwürfe + Veröffentlichtes),
 * inklusive der Aktionen, die nur auf den eigenen Datensätzen erlaubt sind
 * (bearbeiten/veröffentlichen/löschen — alles nur solange status ENTWURF ist,
 * siehe amplify/functions/verkostung-schreiben/handler.ts).
 *
 * Die Liste selbst kommt über den Secondary-Index `erstelltVon`/`erstelltAmTag`
 * (verkostungenNachErstellerUndTag) — der ist genau für diesen Zugriff da und
 * bereits per `allow.ownerDefinedIn('erstelltVon')` auf den eigenen Sub
 * beschränkt (siehe amplify/data/resource.ts).
 */
export function MeineVerkostungen() {
  const { sub } = useRolle();
  const navigate = useNavigate();

  const [tests, setTests] = useState<Test[]>([]);
  const [eisdielen, setEisdielen] = useState<Record<string, EisdieleKurz>>({});
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [aktionId, setAktionId] = useState<string | null>(null);

  const laden = useCallback(() => {
    if (!sub) return;
    setLaedt(true);
    setFehler(null);
    Promise.all([
      client.models.SpaghettiEisTest.verkostungenNachErstellerUndTag(
        { erstelltVon: sub },
        { sortDirection: 'DESC', selectionSet: FELDER, limit: 200 },
      ),
      client.models.Eisdiele.list({ selectionSet: ['id', 'name', 'ort'], limit: 2000 }),
    ])
      .then(([testRes, eisdieleRes]) => {
        if (testRes.errors?.length) throw new Error(testRes.errors[0].message);
        setTests((testRes.data ?? []) as Test[]);
        const map: Record<string, EisdieleKurz> = {};
        for (const e of eisdieleRes.data ?? []) map[e.id] = { id: e.id, name: e.name, ort: e.ort };
        setEisdielen(map);
      })
      .catch((e) => setFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, [sub]);

  useEffect(() => laden(), [laden]);

  const { entwuerfe, veroeffentlicht } = useMemo(() => {
    const entwuerfe = tests.filter((t) => t.status === 'ENTWURF');
    const veroeffentlicht = tests.filter((t) => t.status === 'VEROEFFENTLICHT');
    return { entwuerfe, veroeffentlicht };
  }, [tests]);

  async function veroeffentlichen(t: Test) {
    if (!pflichtnotenVollstaendig(t)) {
      setFehler('Für die Veröffentlichung müssen die vier Pflichtkriterien bewertet sein — bitte erst bearbeiten.');
      return;
    }
    setAktionId(t.id);
    setFehler(null);
    try {
      const { errors } = await client.mutations.eisTestVeroeffentlichen({ id: t.id });
      if (errors?.length) throw new Error(errors[0].message);
      laden();
    } catch (e) {
      setFehler(uebersetzeSchreibFehler(e instanceof Error ? e.message : String(e)));
    } finally {
      setAktionId(null);
    }
  }

  async function loeschen(t: Test) {
    const eisdiele = eisdielen[t.eisdieleId];
    if (!confirm(`Entwurf „${eisdiele?.name ?? 'Verkostung'}" wirklich löschen?`)) return;
    setAktionId(t.id);
    setFehler(null);
    try {
      const { errors } = await client.mutations.eisTestLoeschen({ id: t.id });
      if (errors?.length) throw new Error(errors[0].message);
      laden();
    } catch (e) {
      setFehler(uebersetzeSchreibFehler(e instanceof Error ? e.message : String(e)));
    } finally {
      setAktionId(null);
    }
  }

  return (
    <div className="container">
      <p className="label">Meine Verkostungen</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1>Meine</h1>
        <button className="btn btn-primaer" onClick={() => navigate('/app/neue-verkostung')}>
          + Neue Verkostung
        </button>
      </div>
      <SpaghettiDivider />

      {laedt ? <p className="label">Lädt …</p> : null}
      {fehler ? (
        <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>
          {fehler}
        </p>
      ) : null}

      {!laedt && tests.length === 0 ? (
        <p style={{ color: 'var(--espresso-70)' }}>
          Noch keine eigene Verkostung. Mit „+ Neue Verkostung" oben geht's los.
        </p>
      ) : null}

      {entwuerfe.length > 0 ? (
        <>
          <h2>Entwürfe</h2>
          <p className="hinweis">
            Entwürfe siehst nur du. Erst nach dem Veröffentlichen erscheinen sie in den Listen und lassen sich
            nicht mehr ändern.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {entwuerfe.map((t) => (
              <VerkostungZeile
                key={t.id}
                test={t}
                eisdiele={eisdielen[t.eisdieleId]}
                busy={aktionId === t.id}
                aktionen={
                  <>
                    <Link className="btn btn-sekundaer" to={`/app/meine-verkostungen/${t.id}`}>
                      Bearbeiten
                    </Link>
                    <button
                      className="btn btn-primaer"
                      disabled={aktionId === t.id}
                      onClick={() => veroeffentlichen(t)}
                    >
                      Veröffentlichen
                    </button>
                    <button
                      className="btn btn-sekundaer"
                      disabled={aktionId === t.id}
                      onClick={() => loeschen(t)}
                      style={{ color: 'var(--erdbeere)' }}
                    >
                      Löschen
                    </button>
                  </>
                }
              />
            ))}
          </div>
        </>
      ) : null}

      {veroeffentlicht.length > 0 ? (
        <>
          <h2>Veröffentlicht</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.75rem' }}>
            {veroeffentlicht.map((t) => (
              <VerkostungZeile
                key={t.id}
                test={t}
                eisdiele={eisdielen[t.eisdieleId]}
                aktionen={
                  <Link className="btn btn-sekundaer" to={`/app/verkostungen/${t.id}`}>
                    Ansehen
                  </Link>
                }
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function VerkostungZeile({
  test,
  eisdiele,
  aktionen,
  busy,
}: {
  test: Test;
  eisdiele: EisdieleKurz | undefined;
  aktionen: React.ReactNode;
  busy?: boolean;
}) {
  return (
    <div
      className="karte"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap',
        opacity: busy ? 0.6 : 1,
      }}
    >
      <div>
        <span className="label">{STATUS_LABEL[test.status] ?? test.status}</span>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>
          {eisdiele?.name ?? 'Unbekannte Eisdiele'}
        </div>
        <div style={{ color: 'var(--espresso-70)', fontSize: '0.9rem' }}>
          {eisdiele?.ort ?? ''} · {formatDatum(test.besuchsdatum)}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <GuennieRating wert={test.gesamtGuennies} groesse="sm" />
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>{aktionen}</div>
      </div>
    </div>
  );
}
